import type { ApiClientError, ApiErrorResponse, ApiResult } from '@/types/api';

const PRODUCTION_API_BASE_URL = 'https://api.poker-grind.com';

function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;

  if (configured && configured.length > 0) {
    return configured.replace(/\/$/, '');
  }

  // Production fallback when the SPA is served from poker-grind.com but the build
  // omitted VITE_API_BASE_URL — requests must go to the API subdomain, not the SPA.
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'poker-grind.com' || hostname === 'www.poker-grind.com') {
      return PRODUCTION_API_BASE_URL;
    }
  }

  return '';
}

async function parseErrorResponse(response: Response): Promise<ApiClientError> {
  try {
    const body = (await response.json()) as ApiErrorResponse;
    return {
      statusCode: response.status,
      code: body.error.code,
      message: body.error.message,
    };
  } catch {
    return {
      statusCode: response.status,
      code: 'HTTP_ERROR',
      message: response.statusText || 'Request failed',
    };
  }
}

export class ApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? resolveBaseUrl();
  }

  async get<T>(path: string): Promise<ApiResult<T>> {
    const url = `${this.baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return {
          ok: false,
          error: await parseErrorResponse(response),
        };
      }

      const data = (await response.json()) as T;
      return { ok: true, data };
    } catch (cause) {
      return {
        ok: false,
        error: {
          statusCode: 0,
          code: 'NETWORK_ERROR',
          message: cause instanceof Error ? cause.message : 'Network request failed',
        },
      };
    }
  }
}

export const apiClient = new ApiClient();
